import { Routes } from '@angular/router';
import { adminGuard } from './core/guards/admin.guard';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layout/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [authGuard], // Cualquier staff puede ver
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/users/users.component').then((m) => m.UsersComponent),
        canActivate: [adminGuard], // Solo admin y super_admin
      },
      {
        path: 'deletion-feedback',
        loadComponent: () =>
          import('./features/deletion-feedback/deletion-feedback.component').then(
            (m) => m.DeletionFeedbackComponent
          ),
        canActivate: [adminGuard], // Admin y Super Admin
      },
      {
        path: 'user-feedback',
        loadComponent: () =>
          import('./features/user-feedback/user-feedback.component').then(
            (m) => m.UserFeedbackComponent
          ),
        canActivate: [adminGuard],
      },
      {
        path: 'logs',
        loadComponent: () => import('./features/logs/logs.component').then((m) => m.LogsComponent),
        canActivate: [roleGuard(['analyst', 'admin', 'super_admin'])],
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./features/payments/payments.component').then((m) => m.PaymentsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'settings/unmatch-question',
        loadComponent: () =>
          import('./features/settings/settings.component').then((m) => m.SettingsComponent),
        canActivate: [superAdminGuard], // Solo super_admin
      },
      {
        path: 'settings/pro-perfect-match',
        loadComponent: () =>
          import('./features/settings/settings.component').then((m) => m.SettingsComponent),
        canActivate: [superAdminGuard], // Solo super_admin
      },
      {
        path: 'settings/avatars',
        loadComponent: () =>
          import('./features/settings/settings.component').then((m) => m.SettingsComponent),
        canActivate: [superAdminGuard], // Solo super_admin
      },
      {
        path: 'settings',
        pathMatch: 'full',
        redirectTo: 'settings/unmatch-question',
      },
      {
        path: 'system-control',
        loadComponent: () =>
          import('./features/system-control/system-control.component').then(
            (m) => m.SystemControlComponent
          ),
        canActivate: [superAdminGuard], // Solo super_admin
      },
      {
        path: 'payable-services',
        loadComponent: () =>
          import('./features/payable-services/payable-services.component').then(
            (m) => m.PayableServicesComponent
          ),
        canActivate: [superAdminGuard], // Solo super_admin
      },
      {
        path: 'questions-management',
        loadComponent: () =>
          import('./features/questions-management/questions-management.component').then(
            (m) => m.QuestionsManagementComponent
          ),
        canActivate: [superAdminGuard],
      },
      {
        path: 'compatibility-matrix',
        loadComponent: () =>
          import('./features/compatibility-matrix/compatibility-matrix.component').then(
            (m) => m.CompatibilityMatrixComponent
          ),
        canActivate: [superAdminGuard],
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'pro-matches',
        redirectTo: 'settings/pro-perfect-match',
        pathMatch: 'full',
      },
      {
        path: 'advertising',
        loadComponent: () =>
          import('./features/advertising/advertising.component').then(
            (m) => m.AdvertisingComponent
          ),
        canActivate: [adminGuard], // Admin y Super Admin
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notifications/notifications.component').then(
            (m) => m.NotificationsComponent
          ),
        canActivate: [adminGuard], // Admin y Super Admin
      },
      {
        path: 'app-assets',
        loadComponent: () =>
          import('./features/app-assets/app-assets.component').then(
            (m) => m.AppAssetsComponent
          ),
        canActivate: [superAdminGuard], // Solo super_admin
      },
      {
        path: 'match-laboratory',
        loadComponent: () =>
          import('./features/match-laboratory/match-laboratory.component').then(
            (m) => m.MatchLaboratoryComponent
          ),
        canActivate: [adminGuard], // Admin y Super Admin
      },
      {
        path: 'games-management',
        loadComponent: () =>
          import('./features/games-management/games-management.component').then(
            (m) => m.GamesManagementComponent
          ),
        canActivate: [adminGuard], // Admin y Super Admin
      },
      {
        path: 'experts-admin',
        loadComponent: () =>
          import('./features/experts-admin/experts-admin.component').then(
            (m) => m.ExpertsAdminComponent
          ),
        canActivate: [adminGuard],
      },
      {
        path: 'content',
        loadComponent: () =>
          import('./features/content/content-shell.component').then((m) => m.ContentShellComponent),
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'articles',
            pathMatch: 'full',
          },
          {
            path: 'articles',
            loadComponent: () =>
              import('./features/content/content-articles.component').then(
                (m) => m.ContentArticlesComponent
              ),
          },
          {
            path: 'topics',
            loadComponent: () =>
              import('./features/content/content-topics.component').then(
                (m) => m.ContentTopicsComponent
              ),
          },
          {
            path: 'comments',
            loadComponent: () =>
              import('./features/content/content-comments.component').then(
                (m) => m.ContentCommentsComponent
              ),
          },
        ],
      },
      {
        path: 'documentation',
        loadComponent: () =>
          import('./features/documentation/documentation.component').then(
            (m) => m.DocumentationComponent
          ),
        canActivate: [authGuard], // Cualquier usuario autenticado puede ver documentación
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./features/errors/unauthorized.component').then((m) => m.UnauthorizedComponent),
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
